import type { PageHelp } from '../../help/types';

/** Page guide for `/packages/:code/imports` — bulk upload into a package (spec §9.5, §12A). */
export const packageImportsHelp: PageHelp = {
  title: 'Package imports',
  intro:
    'Bulk upload of a package’s keys and translations from a simple_json file, one locale per file, with the history that makes it safe. Nothing is written until you approve the preview, every applied batch can be reverted, and what it writes reaches every subscribing project at its next publish.',
  sections: ['History', 'Upload'],
  contexts: {
    'upload-dialog': 'Click “Upload” at the top right.',
  },
  tips: [
    'Only keys with one of this package’s prefixes are written. A row with a prefix that another project or package owns is reported as an error and left out.',
    'Importing a package’s keys here is the same as importing them from a subscribing project with “Write keys owned by a subscribed package”, without needing a project at all.',
    'A file may be at most 10 MB and 20,000 rows; split a larger one into several uploads.',
  ],
  steps: [
    {
      selector: '[data-help="imports-upload"]',
      title: 'Start an upload',
      body: 'Opens the three-step import dialog: choose a file and its locale, check the diff, then apply. The button is enabled for owners and translators of the package and for administrators; everyone else only sees the history.',
      section: 'History',
      i18n: {
        'de-AT': {
          title: 'Upload starten',
          body: 'Öffnet den dreistufigen Import-Dialog: Datei und Sprache wählen, Diff prüfen, dann anwenden. Die Schaltfläche ist für Owner und Translator des Pakets sowie für Administratoren aktiv; alle anderen sehen nur die Historie.',
        },
        'sr-Latn-RS': {
          title: 'Pokretanje otpremanja',
          body: 'Otvara dijalog za uvoz u tri koraka: izbor datoteke i jezika, provera razlika, pa primena. Dugme je aktivno za owner i translator ulogu na paketu i za administratore; svi ostali vide samo istoriju.',
        },
      },
    },
    {
      selector: '[data-help="imports-history"]',
      title: 'Read the history',
      body: 'Every upload into this package, newest first, with how many keys it created, how many values it updated and how many rows it skipped. A preview that was never applied stays here and can still be applied.',
      section: 'History',
      i18n: {
        'de-AT': {
          title: 'Historie lesen',
          body: 'Jeder Upload in dieses Paket, der neueste zuerst, mit der Zahl der angelegten Schlüssel, der aktualisierten Werte und der übersprungenen Zeilen. Eine nie angewendete Vorschau bleibt hier und kann noch angewendet werden.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje istorije',
          body: 'Svako otpremanje u ovaj paket, najnovije prvo, sa brojem kreiranih ključeva, ažuriranih vrednosti i preskočenih redova. Pregled koji nikad nije primenjen ostaje ovde i još uvek se može primeniti.',
        },
      },
    },
    {
      selector: '[data-help="imports-diff"]',
      title: 'Open a batch',
      body: 'Shows what the batch did or would do, row by row, including the rows it skipped and why.',
      section: 'History',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Einen Durchgang öffnen',
          body: 'Zeigt Zeile für Zeile, was der Durchgang getan hat oder tun würde, einschließlich der übersprungenen Zeilen und des Grundes.',
        },
        'sr-Latn-RS': {
          title: 'Otvaranje uvoza',
          body: 'Prikazuje red po red šta je uvoz uradio ili bi uradio, uključujući preskočene redove i razlog.',
        },
      },
    },
    {
      selector: '[data-help="imports-revert"]',
      title: 'Revert a batch',
      body: 'Puts every cell the batch wrote back to its previous value, in this package and so in every subscriber. A cell somebody edited since the import is left alone and reported.',
      section: 'History',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Einen Durchgang zurücknehmen',
          body: 'Setzt jede Zelle, die der Durchgang geschrieben hat, auf ihren vorigen Wert zurück, in diesem Paket und damit bei jedem Abonnenten. Eine seit dem Import bearbeitete Zelle bleibt unverändert und wird gemeldet.',
        },
        'sr-Latn-RS': {
          title: 'Poništavanje uvoza',
          body: 'Vraća svaku ćeliju koju je uvoz upisao na prethodnu vrednost, u ovom paketu, a time i kod svakog pretplatnika. Ćelija koju je neko izmenio posle uvoza ostaje netaknuta i biva prijavljena.',
        },
      },
    },
    {
      selector: '[data-help="imports-option-create-keys"]',
      title: 'Create missing keys',
      body: 'On, a key in the file that the package does not have yet is created, as long as its prefix is one of the package’s. Off, the import only updates values and reports unknown keys.',
      section: 'Upload',
      context: 'upload-dialog',
      i18n: {
        'de-AT': {
          title: 'Fehlende Schlüssel anlegen',
          body: 'Eingeschaltet wird ein Schlüssel aus der Datei, den das Paket noch nicht hat, angelegt, sofern sein Präfix eines des Pakets ist. Ausgeschaltet aktualisiert der Import nur Werte und meldet unbekannte Schlüssel.',
        },
        'sr-Latn-RS': {
          title: 'Kreiranje ključeva koji nedostaju',
          body: 'Uključeno: ključ iz datoteke koji paket još nema se kreira, pod uslovom da mu je prefiks jedan od prefiksa paketa. Isključeno: uvoz samo ažurira vrednosti i prijavljuje nepoznate ključeve.',
        },
      },
    },
    {
      selector: '[data-help="imports-option-register-prefixes"]',
      title: 'Claim unknown prefixes',
      body: 'A prefix nobody owns yet is skipped unless this is ticked; ticked, the prefix is registered to this package. Only a package owner or an administrator may use it. Check the prefixes first: a prefix registered by mistake is removed in package settings, and only while no key uses it.',
      section: 'Upload',
      context: 'upload-dialog',
      i18n: {
        'de-AT': {
          title: 'Unbekannte Präfixe übernehmen',
          body: 'Ein Präfix, das noch niemandem gehört, wird übersprungen, außer diese Option ist angehakt; dann wird es diesem Paket zugeordnet. Nur Paket-Owner oder Administratoren dürfen sie nutzen. Prüfen Sie die Präfixe vorher: Ein versehentlich registriertes Präfix entfernen Sie in den Paketeinstellungen, und nur solange kein Schlüssel es verwendet.',
        },
        'sr-Latn-RS': {
          title: 'Preuzimanje nepoznatih prefiksa',
          body: 'Prefiks koji još niko ne poseduje se preskače, osim ako je ovo označeno; tada se prefiks registruje na ovaj paket. Ovo sme samo owner paketa ili administrator. Prvo proverite prefikse: greškom registrovan prefiks uklanja se u podešavanjima paketa, i to samo dok ga nijedan ključ ne koristi.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Paket-Importe',
      intro:
        'Massen-Upload der Schlüssel und Übersetzungen eines Pakets aus einer simple_json-Datei, eine Sprache pro Datei, samt der Historie, die das sicher macht. Nichts wird geschrieben, bevor Sie die Vorschau bestätigen, jeder angewendete Durchgang lässt sich zurücknehmen, und was er schreibt, erreicht jedes abonnierende Projekt bei dessen nächster Veröffentlichung.',
      sections: {
        History: 'Historie',
        Upload: 'Upload',
      },
      contexts: {
        'upload-dialog': 'Klicken Sie rechts oben auf „Upload“.',
      },
      tips: [
        'Geschrieben werden nur Schlüssel mit einem der Präfixe dieses Pakets. Eine Zeile mit einem Präfix, das einem anderen Projekt oder Paket gehört, wird als Fehler gemeldet und ausgelassen.',
        'Die Schlüssel eines Pakets hier zu importieren entspricht dem Import aus einem abonnierenden Projekt mit „Write keys owned by a subscribed package“, nur ohne Umweg über ein Projekt.',
        'Eine Datei darf höchstens 10 MB und 20.000 Zeilen haben; teilen Sie eine größere auf mehrere Uploads auf.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Uvoz u paket',
      intro:
        'Masovno otpremanje ključeva i prevoda paketa iz simple_json datoteke, jedan jezik po datoteci, uz istoriju koja to čini bezbednim. Ništa se ne upisuje dok ne potvrdite pregled, svaki primenjeni uvoz može se poništiti, a ono što upiše stiže do svakog projekta pretplatnika pri njegovom sledećem objavljivanju.',
      sections: {
        History: 'Istorija',
        Upload: 'Otpremanje',
      },
      contexts: {
        'upload-dialog': 'Kliknite na „Upload“ gore desno.',
      },
      tips: [
        'Upisuju se samo ključevi sa jednim od prefiksa ovog paketa. Red sa prefiksom koji poseduje drugi projekat ili paket prijavljuje se kao greška i izostavlja.',
        'Uvoz ključeva paketa ovde isto je što i uvoz iz projekta pretplatnika sa opcijom „Write keys owned by a subscribed package“, samo bez zaobilaženja preko projekta.',
        'Datoteka može imati najviše 10 MB i 20.000 redova; veću podelite na više otpremanja.',
      ],
    },
  },
};
